import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowRight, Loader2, UserPlus, Users, X } from 'lucide-react';
import { clientsAPI, leadsAPI } from '../../utils/api';
import type { Cliente } from '../../types';
import type { LeadClienteBrief } from '../../types/lead';

interface Props {
  isOpen: boolean;
  leadId: string;
  leadName: string;
  onClose: () => void;
  onConverted: (clienteId: string) => void;
}

type Strategia = 'new' | 'existing';

function clienteLabel(c: LeadClienteBrief | Cliente): string {
  // LeadClienteBrief carries denominazione_completa; Cliente doesn't
  if ('denominazione_completa' in c && c.denominazione_completa) {
    return c.denominazione_completa;
  }
  if ((c as Cliente).tipo === 'azienda') {
    return (c as Cliente).ragione_sociale || c.nome || '';
  }
  const full = `${c.nome || ''} ${c.cognome || ''}`.trim();
  return full || c.ragione_sociale || (c.email ?? '') || '';
}

export default function LeadConvertModal({
  isOpen,
  leadId,
  leadName,
  onClose,
  onConverted,
}: Props) {
  const [strategia, setStrategia] = useState<Strategia>('new');
  const [clienteId, setClienteId] = useState<string>('');
  const [copiaActivity, setCopiaActivity] = useState(true);
  const [clienteSearch, setClienteSearch] = useState('');

  // Preview duplicates (only when modal opens)
  const { data: preview, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['lead-conversion-preview', leadId],
    queryFn: async () => {
      const res = await leadsAPI.conversionPreview(leadId);
      return res.data;
    },
    enabled: isOpen,
    staleTime: 0,
  });

  const duplicati = preview?.duplicati_trovati || [];

  // Default strategia: if duplicates found, suggest "existing" + first duplicate
  useEffect(() => {
    if (!isOpen) return;
    if (duplicati.length > 0) {
      setStrategia('existing');
      setClienteId(duplicati[0].id);
    } else {
      setStrategia('new');
      setClienteId('');
    }
  }, [isOpen, duplicati]);

  // Free search over all clients (only when strategia=existing AND user typed something)
  const { data: clientsSearch } = useQuery({
    queryKey: ['clients-search', clienteSearch],
    queryFn: async () => {
      const res = await clientsAPI.getClients({ limit: 20, search: clienteSearch });
      return res.data.data;
    },
    enabled: isOpen && strategia === 'existing' && clienteSearch.length >= 2,
  });

  const convertMutation = useMutation({
    mutationFn: () =>
      leadsAPI.convertLead(leadId, {
        strategia,
        cliente_id: strategia === 'existing' ? clienteId : undefined,
        copia_activity: copiaActivity,
      }),
    onSuccess: (res) => {
      toast.success('Lead convertito');
      onConverted(res.data.cliente_id);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella conversione');
    },
  });

  if (!isOpen) return null;

  const canSubmit =
    !convertMutation.isPending &&
    (strategia === 'new' || (strategia === 'existing' && !!clienteId));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">Converti lead in cliente</h2>
            <p className="text-sm text-gray-500 mt-1">{leadName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Duplicates banner */}
          {isLoadingPreview ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Controllo duplicati...
            </div>
          ) : duplicati.length > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-900 font-medium mb-2">
                Trovati {duplicati.length} possibili duplicati (match su email, C.F. o P.IVA)
              </p>
              <ul className="text-xs text-amber-800 space-y-1">
                {duplicati.slice(0, 5).map((d) => (
                  <li key={d.id}>· {clienteLabel(d)}{d.email ? ` (${d.email})` : ''}</li>
                ))}
                {duplicati.length > 5 && (
                  <li className="italic opacity-70">… e altri {duplicati.length - 5}</li>
                )}
              </ul>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
              Nessun duplicato rilevato. Puoi creare un nuovo cliente in sicurezza.
            </div>
          )}

          {/* Strategia radio */}
          <div className="space-y-3">
            <label
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                strategia === 'new'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="strategia"
                value="new"
                checked={strategia === 'new'}
                onChange={() => setStrategia('new')}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">
                    Crea nuovo cliente
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Creo un nuovo record Cliente copiando l&rsquo;anagrafica del lead.
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                strategia === 'existing'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="strategia"
                value="existing"
                checked={strategia === 'existing'}
                onChange={() => setStrategia('existing')}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">
                    Linka a cliente esistente
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Collego il lead a un cliente già presente senza creare duplicati.
                </p>

                {strategia === 'existing' && (
                  <div className="mt-3 space-y-2">
                    {duplicati.length > 0 && (
                      <select
                        value={clienteId}
                        onChange={(e) => setClienteId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Seleziona un duplicato —</option>
                        {duplicati.map((d) => (
                          <option key={d.id} value={d.id}>
                            {clienteLabel(d)}
                            {d.email ? ` · ${d.email}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    <input
                      type="text"
                      value={clienteSearch}
                      onChange={(e) => setClienteSearch(e.target.value)}
                      placeholder="Cerca altri clienti (nome, email, P.IVA)..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {clientsSearch && clientsSearch.length > 0 && (
                      <ul className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {clientsSearch.map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => setClienteId(c.id)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                clienteId === c.id ? 'bg-blue-50 font-medium' : ''
                              }`}
                            >
                              {clienteLabel(c)}
                              {c.email && (
                                <span className="ml-2 text-xs text-gray-500">· {c.email}</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Copia activity */}
          <label className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={copiaActivity}
              onChange={(e) => setCopiaActivity(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Copia la timeline attività del lead nel cliente
          </label>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-sm"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => convertMutation.mutate()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {convertMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            Converti
          </button>
        </div>
      </div>
    </div>
  );
}
